<template>
  <div>
    <autoLogout />
    <loader :loader="sending" />

    <div class="wrapper-content">
      <v-card>
        <encabezado :elementos="encabezado" />
        <v-card-text>
          <v-container>
            <validation-observer v-slot="{ invalid }">
              <v-form id="editPatient">
                <v-row>
                  <v-col>
                    <h3 class="font-weight-black mb-5">
                      Información de identificación médica
                    </h3>
                  </v-col>
                </v-row>

                <v-row class="mb-3">
                  <v-col cols="12" md="6" sm="12">
                    <validation-provider
                      v-slot="{ errors }"
                      name="CURP"
                      rules="required|max:18|min:18|regex:^[a-zA-Z0-9]+$"
                    >
                      <v-text-field
                        prepend-icon="mdi-account"
                        v-model="formEditar.person.curp"
                        :value="formEditar.person.curp"
                        :counter="18"
                        :maxlength="18"
                        :error-messages="errors"
                        label="CURP"
                        name="curp"
                        id="curp"
                        required
                        @input="upperCase($event)"
                        @blur="validateCurp"
                      ></v-text-field>
                    </validation-provider>
                  </v-col>

                  <v-col cols="12" md="6" sm="12">
                    <validation-provider
                      v-slot="{ errors }"
                      name="Institución aseguradora"
                      rules="required"
                    >
                      <v-autocomplete
                        prepend-icon="mdi-hospital-building"
                        v-model="formEditar.healthInsurance.idHealthInsurance"
                        :value="formEditar.healthInsurance.idHealthInsurance"
                        :items="listHealthInsurance"
                        :item-value="listHealthInsurance.value"
                        :item-text="listHealthInsurance.text"
                        :error-messages="errors"
                        no-data-text="Sin aseguradoras por mostrar"
                        label="Institución aseguradora"
                        return-object
                        required
                      ></v-autocomplete>
                    </validation-provider>
                  </v-col>

                  <template v-if="isImss">
                    <v-col cols="12" md="6" sm="12">
                      <validation-provider
                        v-slot="{ errors }"
                        name="NSS"
                        rules="max:11|min:11|numeric"
                      >
                        <v-text-field
                          prepend-icon="mdi-hospital"
                          v-model="formEditar.imssNumber"
                          :value="formEditar.imssNumber"
                          :counter="11"
                          :error-messages="errors"
                          label="NSS"
                          name="patientNSS"
                          id="patientNSS"
                          required
                        ></v-text-field>
                      </validation-provider>
                    </v-col>

                    <v-col cols="12" md="6" sm="12">
                      <validation-provider
                        v-slot="{ errors }"
                        name="Agregado médico"
                        rules="max:8|min:8|regex:^[a-zA-Z0-9]+$"
                      >
                        <v-text-field
                          prepend-icon="mdi-plus-box-multiple"
                          v-model="formEditar.medicalAggregated"
                          :value="formEditar.medicalAggregated"
                          :counter="8"
                          :error-messages="errors"
                          label="Agregado médico"
                          name="medicalAggregated"
                          id="medicalAggregated"
                          required
                          @input="
                            if (formEditar.medicalAggregated)
                              formEditar.medicalAggregated = $event.toUpperCase();
                          "
                        ></v-text-field>
                      </validation-provider>
                    </v-col>
                  </template>

                  <template v-if="isOther">
                    <v-col cols="12" md="6" sm="12">
                      <validation-provider
                        v-slot="{ errors }"
                        name="Nombre de aseguradora"
                        rules="regex:^[a-zA-Z0-9 ]+$"
                      >
                        <v-text-field
                          v-model="formEditar.nameOther"
                          prepend-icon="mdi-shield"
                          label="Nombre de aseguradora"
                          :error-messages="errors"
                          required
                        ></v-text-field>
                      </validation-provider>
                    </v-col>
                    <v-col cols="12" md="6" sm="12">
                      <validation-provider
                        v-slot="{ errors }"
                        name="Descripción"
                        rules="regex:^[a-zA-Z0-9 ]+$"
                      >
                        <v-text-field
                          v-model="formEditar.description"
                          prepend-icon="mdi-information-variant"
                          label="Descripción"
                          :error-messages="errors"
                          required
                        ></v-text-field>
                      </validation-provider>
                    </v-col>
                  </template>

                  <template v-if="isIssste">
                    <v-col cols="12" md="6" sm="12">
                      <validation-provider
                        v-slot="{ errors }"
                        name="Número de afiliación"
                      >
                        <v-text-field
                          prepend-icon="mdi-pound-box-outline"
                          v-model="formEditar.membershipNumber"
                          label="Número de afiliación"
                          :counter="50"
                          :error-messages="errors"
                          required
                        ></v-text-field>
                      </validation-provider>
                    </v-col>
                  </template>
                </v-row>

                <v-row>
                  <v-col>
                    <h3 class="font-weight-black mb-5">Información personal</h3>
                  </v-col>
                </v-row>

                <v-row class="mb-3">
                  <v-col cols="12" md="3" sm="12">
                    <validation-provider
                      v-slot="{ errors }"
                      name="Nombre(s)"
                      rules="required|max:60|min:2|regex:^[a-zA-ZÀ-ÿ ]+$"
                    >
                      <v-text-field
                        prepend-icon="mdi-account"
                        v-model="formEditar.person.name"
                        :value="formEditar.person.name"
                        :error-messages="errors"
                        label="Nombre(s)"
                        :counter="60"
                        id="name"
                        required
                      ></v-text-field>
                    </validation-provider>
                  </v-col>
                  <v-col cols="12" md="3" sm="12">
                    <validation-provider
                      v-slot="{ errors }"
                      name="Primer apellido"
                      rules="required|max:60|min:2|regex:^[a-zA-ZÀ-ÿ ]+$"
                    >
                      <v-text-field
                        prepend-icon="mdi-card-account-details"
                        v-model="formEditar.person.firstLastname"
                        :value="formEditar.person.firstLastname"
                        label="Primer apellido"
                        :error-messages="errors"
                        :counter="60"
                        id="firstLastname"
                        required
                        @input="capitalizeLastname($event)"
                      ></v-text-field>
                    </validation-provider>
                  </v-col>
                  <v-col cols="12" md="3" sm="12">
                    <validation-provider
                      v-slot="{ errors }"
                      name="Segundo apellido"
                      rules="max:60|min:2|regex:^[a-zA-ZÀ-ÿ ]+$"
                    >
                      <v-text-field
                        prepend-icon="mdi-card-account-details-outline"
                        v-model="formEditar.person.secondLastname"
                        :value="formEditar.person.secondLastname"
                        :error-messages="errors"
                        label="Segundo apellido"
                        id="secondLastName"
                        :counter="60"
                        required
                        @input="capitalizeSurname($event)"
                      ></v-text-field>
                    </validation-provider>
                  </v-col>
                  <v-col cols="12" md="3" sm="12">
                    <validation-provider
                      v-slot="{ errors }"
                      name="Sexo"
                      rules="required"
                    >
                      <v-select
                        prepend-icon="mdi-gender-male-female"
                        v-model="formEditar.person.gender"
                        :value="formEditar.person.gender"
                        :items="genero"
                        :item-text="genero.text"
                        :item-value="genero.value"
                        :error-messages="errors"
                        label="Sexo"
                        required
                      >
                      </v-select>
                    </validation-provider>
                  </v-col>
                </v-row>

                <v-row class="mb-3">
                  <v-col cols="12" md="3" sm="12">
                    <validation-provider
                      v-slot="{ errors }"
                      name="Nacionalidad"
                    >
                      <v-autocomplete
                        prepend-icon="mdi-flag-variant-outline"
                        v-model="formEditar.person.nationality"
                        label="Nacionalidad"
                        :rules="nameRules"
                        :items="nationality"
                        :item-text="nationality.text"
                        :item-value="nationality.value"
                        :error-messages="errors"
                        required
                      ></v-autocomplete>
                    </validation-provider>
                  </v-col>
                  <template
                    v-if="
                      formEditar.person.nationality &&
                        formEditar.person.nationality === 'Otro'
                    "
                  >
                    <v-col cols="3" md="3" sm="12">
                      <validation-provider
                        v-slot="{ errors }"
                        name="Nombre de Nacionalidad"
                        rules="required|alpha"
                      >
                        <v-text-field
                          prepend-icon="mdi-flag-variant-outline"
                          v-model="otherNationality"
                          label="Nombre de Nacionalidad*"
                          :error-messages="errors"
                          :counter="50"
                          required
                        >
                        </v-text-field>
                      </validation-provider>
                    </v-col>
                  </template>
                  <v-col cols="12" md="3" sm="12">
                    <v-menu
                      ref="menu2"
                      v-model="menu2"
                      :close-on-content-click="false"
                      transition="scale-transition"
                      offset-y
                      max-width="290px"
                      min-width="auto"
                    >
                      <template v-slot:activator="{ on, attrs }">
                        <validation-provider
                          v-slot="{ errors }"
                          name="Fecha de nacimiento"
                          rules="required"
                        >
                          <v-text-field
                            id="fechaNac"
                            v-model="dateFormatted"
                            label="Fecha de nacimiento"
                            hint="Formato DD/MM/YYYY"
                            prepend-icon="mdi-calendar"
                            v-bind="attrs"
                            @blur="date2 = parseDate(dateFormatted)"
                            v-on="on"
                            :error-messages="errors"
                          ></v-text-field>
                        </validation-provider>
                      </template>
                      <v-date-picker
                        :max-date="new Date()"
                        v-model="date2"
                        @input="menu2 = false"
                        locale="es"
                        :active-picker.sync="activePicker"
                        min="1950-01-01"
                      ></v-date-picker>
                    </v-menu>
                  </v-col>
                  <v-col
                    v-if="formEditar.person.nationality != 'Otro'"
                    cols="12"
                    md="3"
                    sm="12"
                  >
                    <validation-provider
                      v-slot="{ errors }"
                      name="Entidad federativa de nacimiento"
                      rules="required"
                    >
                      <v-autocomplete
                        prepend-icon="fas fa-map-pin"
                        label="Entidad federativa de nacimiento"
                        v-model="formEditar.person.birthFederalEntity"
                        :error-messages="errors"
                        :items="birthFederalEntities"
                        :item-text="birthFederalEntities.text"
                        :item-value="birthFederalEntities.value"
                        required
                      ></v-autocomplete>
                    </validation-provider>
                  </v-col>
                  <v-col cols="12" md="3" sm="12">
                    <validation-provider
                      v-slot="{ errors }"
                      name="Estado civil"
                    >
                      <v-autocomplete
                        prepend-icon="mdi-account-heart"
                        v-model="formEditar.person.maritalStatus"
                        label="Estado civil"
                        :items="estadoCivil"
                        :item-text="estadoCivil.text"
                        :item-value="estadoCivil.value"
                        :error-messages="errors"
                      >
                      </v-autocomplete>
                    </validation-provider>
                  </v-col>
                  <v-col cols="12" md="3" sm="12">
  <validation-provider
    v-slot="{ errors }"
    name="Lengua indígena"
    rules="required"
  >
    <v-select
      prepend-icon="mdi-tilde"
      v-model="formEditar.person.indigenousLanguage"
      :items="languages"
      :item-text="item => item.text"
      :item-value="item => item.value"
      label="Lengua indígena"
      :error-messages="errors"
      required
    />
  </validation-provider>
</v-col>
                  
                </v-row>

                <v-row>
                  <v-col>
                    <h3 class="font-weight-black mb-5">
                      Información de contacto
                    </h3>
                  </v-col>
                </v-row>

                <v-row>
                  <v-col cols="12" md="8" sm="12">
                    <validation-provider
                      v-slot="{ errors }"
                      name="Calle"
                      rules="required|max:100|regex:^[a-zA-Z0-9 ]"
                    >
                      <v-text-field
                        prepend-icon="fas fa-road"
                        v-model="formEditar.person.address.street"
                        label="Calle"
                        :counter="100"
                        :error-messages="errors"
                        id="directionStreet"
                        required
                      ></v-text-field>
                    </validation-provider>
                  </v-col>
                  <v-col cols="12" md="2" sm="12">
                    <validation-provider
                      v-slot="{ errors }"
                      name="Número interior"
                      rules="max:5|regex:^[a-zA-Z0-9 ]+$"
                    >
                      <v-text-field
                        prepend-icon="mdi-pound"
                        v-model="formEditar.person.address.outdoorNumber"
                        :counter="5"
                        :error-messages="errors"
                        label="Número exterior"
                        required
                      ></v-text-field>
                    </validation-provider>
                  </v-col>
                  <v-col cols="12" md="2" sm="12">
                    <validation-provider
                      v-slot="{ errors }"
                      name="Número interior"
                      rules="max:5|regex:^[a-zA-Z0-9 ]+$"
                    >
                      <v-text-field
                        prepend-icon="mdi-pound-box-outline"
                        v-model="formEditar.person.address.interiorNumber"
                        label="Número interior"
                        :error-messages="errors"
                        :counter="5"
                      ></v-text-field>
                    </validation-provider>
                  </v-col>
                </v-row>

                <v-row>
                  <v-col cols="12" md="3" sm="12">
                    <validation-provider
                      v-slot="{ errors }"
                      name="Código postal"
                      rules="required|max:5|min:1|numeric|regex:^[0-9]+$"
                    >
                      <v-text-field
                        prepend-icon="mdi-mailbox-outline"
                        v-model="formEditar.person.address.postalCode"
                        :value="formEditar.person.address.postalCode"
                        label="Código postal"
                        :counter="5"
                        maxlength="5"
                        :error-messages="errors"
                        id="directionPostalCode"
                        @change="findAddress"
                        required
                      ></v-text-field>
                    </validation-provider>
                  </v-col>
                  <v-col cols="12" md="3" sm="12" v-if="!noPostal">
                    <validation-provider
                      v-slot="{ errors }"
                      name="Entidad federativa"
                      rules="required"
                    >
                      <v-autocomplete
                        prepend-icon="mdi-map-marker-radius"
                        v-model="formEditar.person.address.federalEntityKey"
                        :items="federalEntities"
                        :item-text="federalEntities.text"
                        :item-value="federalEntities.value"
                        :error-messages="errors"
                        label="Entidad federativa"
                        id="directionState"
                        required
                        :disabled="validAdress"
                      ></v-autocomplete>
                    </validation-provider>
                  </v-col>
                  <v-col cols="12" md="3" sm="12" v-if="!noPostal">
                    <validation-provider
                      v-slot="{ errors }"
                      name="Municipio"
                      rules="required"
                    >
                      <v-autocomplete
                        prepend-icon="mdi-map-outline"
                        v-model="formEditar.person.address.municipalityKey"
                        :value="formEditar.person.address.municipalityKey"
                        :items="municipalities"
                        :item-text="municipalities.text"
                        :item-value="municipalities.value"
                        :error-messages="errors"
                        label="Municipio"
                        id="directionCity"
                        required
                        :disabled="validAdress"
                      ></v-autocomplete>
                    </validation-provider>
                  </v-col>
                  <v-col cols="12" md="3" sm="12" v-if="!noPostal">
                    <validation-provider
                      v-slot="{ errors }"
                      name="Colonia"
                      rules="required"
                    >
                      <v-autocomplete
                        prepend-icon="mdi-map-marker"
                        v-model="formEditar.person.address.colonyKey"
                        :value="formEditar.person.address.colonyKey"
                        :items="colonies"
                        :error-messages="errors"
                        label="Colonia"
                        id="directionColony"
                        required
                        :disabled="validAdress"
                        @change="checkColony"
                      ></v-autocomplete>
                    </validation-provider>
                  </v-col>
                  <v-col cols="12" md="3" sm="12" v-if="noPostal">
                    <validation-provider
                      v-slot="{ errors }"
                      name="Entidad federativa"
                      rules="required"
                    >
                      <v-text-field
                        prepend-icon="mdi-map-marker-radius"
                        v-model="formEditar.person.address.federalEntityKey"
                        label="Entidad federativa de residencia*"
                        :error-messages="errors"
                        required
                      ></v-text-field>
                    </validation-provider>
                  </v-col>

                  <v-col cols="12" md="3" sm="12" v-if="noPostal">
                    <validation-provider
                      v-slot="{ errors }"
                      name="Municipio"
                      rules="required"
                    >
                      <v-text-field
                        prepend-icon="mdi-map-outline"
                        v-model="formEditar.person.address.municipalityKey"
                        label="Municipio*"
                        :error-messages="errors"
                        required
                      ></v-text-field>
                    </validation-provider>
                  </v-col>

                  <v-col cols="12" md="3" sm="12" v-if="noPostal || noColony">
                    <validation-Provider
                      v-slot="{ errors }"
                      name="Colonia"
                      rules="required"
                    >
                      <v-text-field
                        prepend-icon="mdi-map-marker"
                        v-model="otherColony"
                        label="Colonia*"
                        :error-messages="errors"
                        required
                        :counter="100"
                        :maxlength="100"
                      ></v-text-field>
                    </validation-Provider>
                  </v-col>
                </v-row>

                <v-row class="mb-3">
                  <v-col cols="12" md="4" sm="12">
                    <validation-provider
                      v-slot="{ errors }"
                      name="Correo electrónico"
                      rules="email"
                    >
                      <v-text-field
                        prepend-icon="mdi-at"
                        v-model="formEditar.person.contactInformation.email"
                        :value="formEditar.person.contactInformation.email"
                        :error-messages="errors"
                        label="Correo electrónico"
                        id="correo"
                        :counter="50"
                        :maxlength="50"
                        type="email"
                        @input="
                          if (objAltaPaciente.email)
                            objAltaPaciente.email = $event.toLowerCase();
                        "
                      ></v-text-field>
                    </validation-provider>
                  </v-col>
                  <v-col cols="12" md="4" sm="12">
                    <validation-provider
                      v-slot="{ errors }"
                      name="Número telefónico movil"
                      rules="max:10|min:10|numeric|regex:^[0-9]+$"
                    >
                      <v-text-field
                        prepend-icon="mdi-cellphone"
                        v-model="formEditar.person.contactInformation.phone"
                        :value="formEditar.person.contactInformation.phone"
                        :error-messages="errors"
                        label="Número telefónico móvil"
                        id="mobilePhone"
                        :counter="10"
                        :maxlength="10"
                        required
                      ></v-text-field>
                    </validation-provider>
                    <!-- <p class="caption text--secondary">
                            Si desconoce el número telefónico utiliza el
                            formato 0000000000
                          </p> -->
                  </v-col>
                  <v-col cols="12" md="4" sm="12">
                    <validation-provider
                      v-slot="{ errors }"
                      name="Número telefónico fijo"
                      rules="max:10|min:10|numeric|regex:^[0-9]+$"
                    >
                      <v-text-field
                        prepend-icon="mdi-phone"
                        v-model="formEditar.person.contactInformation.homePhone"
                        :value="formEditar.person.contactInformation.homePhone"
                        :error-messages="errors"
                        label="Número telefónico fijo (Opcional)"
                        id="mobilePhone"
                        :counter="10"
                        :maxlength="10"
                      ></v-text-field>
                    </validation-provider>
                  </v-col>
                </v-row>

                <v-row>
                  <v-col>
                    <h3 class="font-weight-black mb-5">Atecedentes</h3>
                  </v-col>
                </v-row>

                <v-row class="mb-3">
                  <v-expansion-panels v-model="panel" multiple>
                    <v-expansion-panel
                      v-for="(item, index) in antecedents"
                      :key="index"
                    >
                      <v-expansion-panel-header>
                        {{ item.name }}
                      </v-expansion-panel-header>
                      <v-expansion-panel-content>
                        <v-row align="baseline" justify="center">
                          <v-col
                            v-for="(question, subindex) in item.questions"
                            :key="subindex"
                          >
                            <span>{{ question.name }}</span>
                            <validation-provider
                              v-slot="{ errors }"
                              :name="question.name"
                              :rules="question.required == 1 ? 'required' : ''"
                            >
                              <v-radio-group
                                v-if="
                                  question.typeAnswer == 2 ||
                                    question.typeAnswer == 4
                                "
                                v-model="question.idAnswer"
                                :error-messages="errors"
                                :required="question.required == 1"
                              >
                                <v-radio
                                  v-for="(answer,
                                  sub_subindex) in question.answers"
                                  :key="sub_subindex"
                                  :value="answer.idAnswer"
                                  :label="answer.name"
                                />
                              </v-radio-group>

                              <v-autocomplete
                                v-if="question.typeAnswer == 3"
                                v-model="question.selectedAnswers"
                                :items="question.answers"
                                item-text="name"
                                item-value="idAnswer"
                                :error-messages="errors"
                                auto-select-first
                                chips
                                clearable
                                deletable-chips
                                multiple
                                small-chips
                                dense
                                :required="question.required == 1"
                              />

                              <v-text-field
                                v-if="
                                  (question.typeAnswer == 1 ||
                                    question.typeAnswer == 4) &&
                                    question.typeData != 6
                                "
                                v-model="question.answer"
                                :error-messages="errors"
                                :type="question.typeDataText"
                                :required="question.required == 1"
                                autocomplete="off"
                                dense
                                clearable
                              />

                              <v-textarea
                                v-if="
                                  (question.typeAnswer == 1 ||
                                    question.typeAnswer == 4) &&
                                    question.typeData == 6
                                "
                                v-model="question.answer"
                                :error-messages="errors"
                                outlined
                                dense
                                clearable
                              />
                            </validation-provider>
                          </v-col>
                        </v-row>
                      </v-expansion-panel-content>
                    </v-expansion-panel>
                  </v-expansion-panels>
                </v-row>

                <v-row>
                  <v-col>
                    <h3 class="font-weight-black mb-5">Fotografía</h3>
                  </v-col>
                </v-row>

                <v-row align="center" justify="center">
                  <v-col class="text-center" cols="12" md="4" sm="12">
                    <v-img
                      class="mx-auto mb-7"
                      max-width="350"
                      :src="formEditar.person.photoPerson"
                    ></v-img>
                    <h3>
                      {{
                        formEditar.person.photoPerson
                          ? "Fotografía actual"
                          : "El paciente no cuenta con fotografía"
                      }}
                    </h3>
                  </v-col>

                  <v-col cols="12" md="4" sm="12">
                    <CameraSuim :videoTypes="['camera']" ref="cameraSUIM" />
                  </v-col>
                </v-row>

                <v-row justify="center" align="center">
                  <v-col class="text-center">
                    <v-btn
                      color="primary"
                      @click="editPatient"
                      :disabled="invalid || invalidDate || sending"
                    >
                      Guardar cambios
                      <v-icon right>mdi-content-save</v-icon>
                    </v-btn>
                  </v-col>
                </v-row>
              </v-form>
            </validation-observer>
          </v-container>
        </v-card-text>
      </v-card>

      <!--Información de usuario con CURP repetido -->
      <v-dialog v-model="modelInfoPatient" persistent max-width="1000">
        <v-card>
          <v-card-title class="text-h6 text-center error--text">
            Se encontró {{ patientInfoList.length }} usuario(s) con la misma
            CURP ingresada
          </v-card-title>
          <validation-observer ref="observer">
            <v-card-text>
              <v-expansion-panels v-model="panelPatient" class="mb-10">
                <v-expansion-panel
                  v-for="(patientInfo, index) in patientInfoList"
                  :key="index"
                >
                  <v-expansion-panel-header>
                    <span>
                      <v-icon left color="grey">mdi-account-search </v-icon
                      >Paciente {{ index + 1 }}
                    </span>
                    <template v-slot:actions>
                      <v-icon color="primary"> $expand </v-icon>
                    </template>
                  </v-expansion-panel-header>
                  <v-expansion-panel-content>
                    <v-container>
                      <v-row>
                        <v-col>
                          <h3 class="font-weight-black">
                            Información personal
                          </h3>
                        </v-col>
                      </v-row>
                      <v-row>
                        <v-col>
                          <v-text-field
                            :value="patientInfo.curp"
                            label="CURP"
                            disabled
                          ></v-text-field>
                        </v-col>
                        <v-col>
                          <v-text-field
                            :value="patientInfo.fullName"
                            label="Nombre"
                            disabled
                          ></v-text-field>
                        </v-col>
                        <v-col>
                          <v-text-field
                            :value="patientInfo.sexText"
                            label="Sexo"
                            disabled
                          ></v-text-field>
                        </v-col>
                      </v-row>
                      <v-row>
                        <v-col>
                          <v-text-field
                            :value="patientInfo.birthDate"
                            label="Fecha de nacimiento"
                            disabled
                          ></v-text-field>
                        </v-col>
                        <v-col>
                          <v-text-field
                            :value="patientInfo.birthFederalEntity"
                            label="Entidad federativa de nacimiento"
                            disabled
                          ></v-text-field>
                        </v-col>
                      </v-row>
                      <v-row>
                        <v-col>
                          <h3 class="font-weight-black">
                            Información de contacto
                          </h3>
                        </v-col>
                      </v-row>
                      <v-row>
                        <v-col>
                          <v-text-field
                            rows="1"
                            :value="patientInfo.address"
                            label="Dirección"
                            id="cancellation"
                            disabled
                          ></v-text-field>
                        </v-col>
                      </v-row>
                      <v-row>
                        <v-col>
                          <v-text-field
                            :value="patientInfo.phone"
                            label="Número telefónico móvil"
                            disabled
                          ></v-text-field>
                        </v-col>
                        <v-col>
                          <v-text-field
                            :value="patientInfo.homePhone"
                            label="Número telefónico fijo"
                            disabled
                          ></v-text-field>
                        </v-col>
                      </v-row>
                    </v-container>
                  </v-expansion-panel-content>
                </v-expansion-panel>
              </v-expansion-panels>
            </v-card-text>
            <v-card-actions class="text-center">
              <v-col>
                <v-btn color="primary" plain @click="modelInfoPatient = false">
                  Continuar <v-icon right>mdi-arrow-right</v-icon>
                </v-btn>
              </v-col>
            </v-card-actions>
          </validation-observer>
        </v-card>
      </v-dialog>
    </div>
  </div>
</template>

<script>
import {
  PatientRequest,
  CatalogueRequest,
  FederalEntityRequest,
  MunicipalityRequest,
  ColonyRequest,
  HealthInsuranceRequest,
  SepomexRequest,
  EvaluationRequest,
} from "@/core/request";
import { extend, ValidationObserver, ValidationProvider } from "vee-validate";
import AutoLogout from "../components/AutoLogout.vue";
import CameraSuim from "@/components/CameraSuim.vue";
import {
  required,
  max,
  min,
  regex,
  email,
  length,
  numeric,
} from "vee-validate/dist/rules";
import Sidebar from "../components/Sidebar.vue";
import moment from "moment";
import Loader from "../components/Loader.vue";
import Encabezado from "@/app/components/base/header/Header.vue";
import { mapActions } from "vuex";
import UtilFront from "../core/UtilFront";

extend("required", {
  ...required,
  message: "{_field_} no puede quedar vacío",
});

extend("regex", {
  ...regex,
  message: "{_field_} no debe contener caracteres especiales o espacios",
});

extend("email", {
  ...email,
  message: "Introduzca un formato valido para {_field_}",
});

extend("length", {
  ...length,
  message: "{_field_} no debe ser mayor o menor a {length} digitos",
});

extend("max", {
  ...max,
  message: "{_field_} no debe ser mayor de {length} caracteres",
});

extend("min", {
  ...min,
  message: "{_field_} no debe ser menor a {length} caracteres",
});

extend("numeric", {
  ...numeric,
  message: "{_field_} solo debe contener valores numéricos",
});

let token = sessionStorage.tokenUser;

export default {
  components: {
    Sidebar,
    Loader,
    ValidationObserver,
    ValidationProvider,
    Encabezado,
    AutoLogout,
    CameraSuim,
  },
  name: "EditarPaciente",
  data: () => ({
    imagePatient: null,
    //Antecedentes
    antecedents: [],
    panel: 0,
    idPerson: "",
    isImss: false,
    isOther: false,
    isIssste: false,
    flag: true,
    loader: false,
    sending: false,
    validAdress: false,
    date2: null,
    menu2: false,
    activePicker: null,
    nameRules: [(v) => !!v || "Campo requerido"],
    encabezado: { icono: "mdi-account", texto: "Editar paciente" },
    headerModalView: {
      icono: "mdi-account-details",
      texto: "Información del paciente",
    },
    formEditar: {
      healthInsurance: {
        idHealthInsurance: {},
      },
      person: {
        contactInformation: {},
        address: {},
        nationality: {},
        indigenousLanguage: {},
        disability: {},
      },
    },
    genero: [
      { text: "Femenino", value: "M" },
      { text: "Masculino", value: "H" },
      { text: "Binario", value: "X" },
    ],
    estadoCivil: [
      { text: "Soltero(a)", value: 1 },
      { text: "Casado(a)", value: 2 },
      { text: "Divorciado(a)", value: 3 },
      { text: "Viudo(a)", value: 4 },
    ],
    federalEntities: [],
    birthFederalEntities: [],
    municipalities: [],
    colonies: [],
    nationality: [
      { text: "Mexicana", value: "Mexicana" },
      { text: "Otra", value: "Otro" },
    ],
    status: [
      { text: "Activo", value: 1 },
      { text: "Inactivo", value: 0 },
    ],
    dateFormatted: null,
    invalidDate: false,
    listHealthInsurance: [],
    patientInfoList: [],
    modelInfoPatient: false,
    panelPatient: 0,
    noPostal: false,
    noColony: false,
    otherColony: "",
    first: 0,
    indigenousLanguage: null,
    disability: null,
    languages: [
    { text: "Sí", value: 1 },
    { text: "No", value: 0 },
  ],
  }),
  created() {
    this.validBrowserTab();
    this.getPatient();
    this.listAllEntities();
    this.listAllHealthInsurances();
  },
  computed: {
    computedDateFormatted() {
      return this.formatDate(this.date2);
    },
    membershipNumber: function() {
      if (this.formEditar.healthInsurance.name === "IMSS") {
        return false;
      } else if (this.formEditar.healthInsurance.name === "No aplica") {
        return false;
      } else if (this.formEditar.healthInsurance.name !== "IMSS") {
        return true;
      }
    },
  },
  watch: {
    date2(val) {
      this.dateFormatted = this.formatDate(this.date2);
      let result = moment(this.dateFormatted, "DD/MM/YYYY", true).isValid();
      let today = moment(new Date()).format("YYYY-MM-DD");
      if (result) {
        if (this.date2 < today) {
          this.invalidDate = false;
          document.getElementById("fechaNac").style = "none";
        } else {
          this.invalidDate = true;
          this.$utilFront.notifications(
            "La fecha no puede ser mayor a la fecha actual",
            "ERROR"
          );
          document.getElementById("fechaNac").style.color = "red";
        }
      } else {
        this.invalidDate = true;
        this.$utilFront.notifications("Formato de fecha incorrecto", "ERROR");
        document.getElementById("fechaNac").style.color = "red";
      }
    },
    "formEditar.healthInsurance.idHealthInsurance": function(val) {
      if (val.text) {
        if (val.text === "IMSS") {
          this.isImss = true;
          this.isOther = false;
          this.isIssste = false;
          this.formEditar.membershipNumber = "";
        } else if (val.text === "NO APLICA") {
          this.isImss = false;
          this.isOther = false;
          this.isIssste = false;
          this.formEditar.imssNumber = "";
          this.formEditar.medicalAggregated = "";
          this.formEditar.membershipNumber = "";
        } else if (val.text === "OTRO") {
          this.isImss = false;
          this.isOther = true;
          this.isIssste = true;
          this.formEditar.imssNumber = "";
          this.formEditar.medicalAggregated = "";
        } else {
          this.isImss = false;
          this.isOther = false;
          this.isIssste = true;
          this.formEditar.imssNumber = "";
          this.formEditar.medicalAggregated = "";
        }
      }
    },
    "formEditar.healthInsurance.name": function(val) {
      if (val) {
        if (val === "IMSS") {
          this.isImss = true;
          this.isOther = false;
          this.isIssste = false;
          this.formEditar.membershipNumber = "";
        } else if (val === "NO APLICA") {
          this.isImss = false;
          this.isOther = false;
          this.isIssste = false;
          this.formEditar.imssNumber = "";
          this.formEditar.medicalAggregated = "";
          this.formEditar.membershipNumber = "";
        } else if (val === "OTRO") {
          this.isImss = false;
          this.isOther = true;
          this.isIssste = true;
          this.formEditar.imssNumber = "";
          this.formEditar.medicalAggregated = "";
        } else {
          this.isImss = false;
          this.isOther = false;
          this.isIssste = true;
          this.formEditar.imssNumber = "";
          this.formEditar.medicalAggregated = "";
        }
      }
    },
  },

  methods: {
    ...mapActions(["validTab", "unauthorized"]),
    validBrowserTab() {
      this.validTab();
    },
    formatDate(date) {
      if (!date) return null;

      const [year, month, day] = date.split("-");
      return `${day}/${month}/${year}`;
    },
    parseDate(date) {
      if (!date) return null;

      const [day, month, year] = date.split("/");
      return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
    },
    upperCase(e) {
      if (this.formEditar.person.curp) {
        this.formEditar.person.curp = e.toUpperCase();
      }
    },
    capitalizeName(e) {
      // const strName = e;

      // const arr = strName.split(" ");

      // for (let i = 0; i < arr.length; i++) {
      //   arr[i] = arr[i].charAt(0).toUpperCase() + arr[i].slice(1).toLowerCase();
      // }

      // const newStrName = arr.join(" ");

      // this.formEditar.person.name = newStrName;
    },
    capitalizeLastname(e) {
      const strName = e;

      const arr = strName.split(" ");

      for (let i = 0; i < arr.length; i++) {
        arr[i] = arr[i].charAt(0).toUpperCase() + arr[i].slice(1).toLowerCase();
      }

      const newStrName = arr.join(" ");

      this.formEditar.person.firstLastname = newStrName;
    },
    capitalizeSurname(e) {
      const strName = e;

      const arr = strName.split(" ");

      for (let i = 0; i < arr.length; i++) {
        arr[i] = arr[i].charAt(0).toUpperCase() + arr[i].slice(1).toLowerCase();
      }

      const newStrName = arr.join(" ");

      this.formEditar.person.secondLastname = newStrName;
    },
    saveNac(date2) {
      this.$refs.menu2.save(date2);
    },
    getPatient() {
      let id = this.$route.params.id;

      PatientRequest.listPatientById(
        { idPatient: id },
        { headers: { Authorization: token } }
      ).then((res) => {
        this.loader = true;
        if (res.data.code === 200 && res.data.status === "OK") {
          this.formEditar = res.data.data;
          this.otherColony = this.formEditar.person.address.colonyKey;
          this.findAddress();
          this.date2 = moment(
            new Date(this.formEditar.person.birthDate)
          ).format("YYYY-MM-DD");
          this.dateFormatted = this.date2;
          this.loader = false;
          this.idPerson = res.data.data.person.idPerson;
          this.getAntecents();
          /*Iniciar address*/

          if (this.formEditar.person.nationality != "Mexicana") {
            this.otherNationality = this.formEditar.person.nationality;
            this.formEditar.person.nationality = "Otro";
          }
        } else if (res.data.code === 401) {
          this.unauthorized();
        } else {
          this.loader = false;
          this.$utilFront.notifications(
            "Error al consultar datos del paciente",
            "ERROR"
          );
          this.$router.go(-1);
        }
      });
    },
    /// Antecente -> inicio
    getAntecents() {
      this.sending = true;
      this.antecedents = [];
      EvaluationRequest.listByPersonAndType({
        type: 1,
        idPerson: this.idPerson,
      })
        .then((res) => {
          let { data, response, message } = res.data;
          if (response) {
            this.antecedents = data;
            this.panel = this.antecedents.map((it, i) => {
              return i;
            });
            setTimeout(
              function() {
                this.panel = [0];
              }.bind(this),
              1000
            );
          } else {
            this.antecedents = [];
          }
        })
        .finally(() => {
          this.sending = false;
        });
    },

    // Antecente -> fin
    async editPatient() {
      this.sending = true;
      let obj = this.formEditar;
      let enti;
      let nan;
      let idee = "";
      if (obj.imssNumber === "") {
        idee = "";
        obj.imssNumber = "";
      } else {
        idee = obj.imssNumber + obj.medicalAggregated;
      }
      if (obj.person.nationality === "Otro") {
        enti = "Ninguno";
        nan = this.otherNationality;
      } else {
        enti = obj.person.birthFederalEntity;
        nan = obj.person.nationality;
      }
      if (this.noPostal || this.noColony) {
        obj.person.address.colonyKey = this.otherColony;
      }
      this.imagePatient = await this.$refs.cameraSUIM.getImage64();

      let updatePatient = {
        idPatient: obj.idPatient,
        idee: idee,
        imssNumber: obj.imssNumber,
        patientNumber: "",
        medicalAggregated: obj.medicalAggregated,
        registrationDate: moment(new Date(obj.registrationDate)).format(),

        membershipNumber: obj.membershipNumber,
        healthInsurance: {
          idHealthInsurance:
            obj.healthInsurance.idHealthInsurance.value ||
            obj.healthInsurance.idHealthInsurance,
          name: obj.nameOther,
          description: obj.description,
        },

        person: {
          idPerson: obj.person.idPerson,
          name: obj.person.name,
          firstLastname: obj.person.firstLastname,
          secondLastname: obj.person.secondLastname,
          curp: obj.person.curp,
          gender: obj.person.gender,
          birthDate: moment(this.date2).format(),
          birthFederalEntity: enti,
          maritalStatus: obj.person.maritalStatus,
          status: obj.person.status,
          contactInformation: {
            idContactInformation:
              obj.person.contactInformation.idContactInformation,
            email: obj.person.contactInformation.email,
            phone: obj.person.contactInformation.phone,
            homePhone: obj.person.contactInformation.homePhone,
          },
          branchOffice: {
            idBranchOffice: this.$utilFront.getIdBranchOffice(),
          },
          address: {
            idAddress: obj.person.address.idAddress,
            interiorNumber: obj.person.address.interiorNumber,
            outdoorNumber: obj.person.address.outdoorNumber,
            postalCode: obj.person.address.postalCode,
            street: obj.person.address.street,
            federalEntityKey: obj.person.address.federalEntityKey,
            municipalityKey: obj.person.address.municipalityKey,
            colonyKey: obj.person.address.colonyKey,
          },
          nationality: nan,
          indigenousLanguage: obj.person.indigenousLanguage,
          disability: obj.person.disability,
        },
        evaluations: this.antecedents,
        imagePatient: this.imagePatient,
      };

      PatientRequest.updatePatient(updatePatient)
        .then((res) => {
          let { data, response, message } = res.data;

          if (response) {
            this.$utilFront.notifications(
              "Información actualizada.",
              "SUCCESS"
            );
            this.$router.go(-1);
          } else {
            message = message || "Ocurrió un error al guardar.";
            this.$utilFront.notifications(message, "ERROR");
          }
        })
        .finally(() => {
          this.sending = false;
        });
    },
    //Catalogos
    listAllEntities() {
      FederalEntityRequest.listAllFederalEntity({
        headers: { Authorization: token },
      }).then((res) => {
        if (res.data.code === 200 && res.data.status === "OK") {
          // this.federalEntities = res.data.data.map((entity) => {
          //   return {
          //     text: entity.name,
          //     value: entity.federalEntityKey,
          //   };
          // });
          this.birthFederalEntities = res.data.data.map((entity) => {
            return {
              text: entity.name,
              value: entity.name,
            };
          });
        } else if (res.data.code === 401) {
          this.unauthorized();
        }
      });
    },
    // listAllColoniesByMunicipality() {
    //   this.isLoading = true;
    //   let idMunicipality = this.formEditar.person.address.municipalityKey;

    //   ColonyRequest.listAllColony(
    //     { municipality: { idMunicipality: idMunicipality } },
    //     { headers: { Authorization: token } }
    //   ).then((res) => {
    //     if (res.data.code === 200 && res.data.status === "OK") {
    //       this.colonies = res.data.data.map((colony) => {
    //         this.isLoading = false;
    //         return {
    //           text: colony.name,
    //           value: colony.colonyKey,
    //         };
    //       });
    //     } else if (res.data.code === 401) {
    //       this.unauthorized();
    //     }
    //   });
    // },
    listAllHealthInsurances() {
      HealthInsuranceRequest.listAllHealthInsurance({
        headers: { Authorization: token },
      }).then((res) => {
        if (res.data.code === 200 && res.data.status === "OK") {
          this.listHealthInsurance = res.data.data.map((insurance) => {
            return {
              text: insurance.name,
              value: insurance.idHealthInsurance,
            };
          });
        } else if (res.data.code === 401) {
          this.unauthorized();
        }
      });
    },
    findAddress() {
      if (this.formEditar?.person?.address?.postalCode.length == 5) {
        this.noColony = false;
        let postalCode = this.formEditar.person.address.postalCode;
        this.sending = true;
        this.validAdress = false;
        this.federalEntities = [];
        this.municipalities = [];
        this.colonies = [];

        SepomexRequest.listAllByPostalCode({ postalCode: `${postalCode}` })
          .then((res) => {
            let { data, response, message } = res.data;

            // this.validAdress = response;

            if (response) {
              this.noPostal = false;
              this.federalEntities = data
                .map((it) => {
                  this.formEditar.person.address.federalEntityKey =
                    it.federalEntity;
                  return it.federalEntity;
                })
                .filter(this.onlyUnique);

              this.municipalities = data
                .map((it) => {
                  this.formEditar.person.address.municipalityKey =
                    it.municipality;
                  return it.municipality;
                })
                .filter(this.onlyUnique);

              this.colonies = data
                .map((it) => {
                  this.formEditar.person.address.colonyKey = it.settlement;
                  return it.settlement;
                })
                .filter(this.onlyUnique);
            } else {
              this.noPostal = true;

              this.$utilFront.notifications(
                "Código postal no registrado, capturar manualmente.",
                "WARNING"
              );
            }
          })
          .finally(() => {
            this.colonies.push("Otro");
            this.checkFirstDirection();
            this.sending = false;
          });
      }
    },
    onlyUnique(value, index, self) {
      return self.indexOf(value) === index;
    },
    validateCurp(e) {
      let data = {
        person: { curp: this.formEditar.person.curp },
      };
      PatientRequest.validCurpPatient(data).then((res) => {
        if (res.data.code === 200 && typeof res.data.data == "object") {
          if (res.data.data != null) {
            this.patientInfoList = res.data.data.filter((d) => {
              if (d.idPatient != this.formEditar.idPatient) {
                d.birthDate =
                  moment(new Date(d.birthDate))
                    .locale("es")
                    .format("DD/MM/YYYY") || "---";
                //d.phone = (res.data.data.phone != null? res.data.data.phone: "ND")
                return d;
              }
            });
            if (this.patientInfoList.length > 0) {
              this.modelInfoPatient = true;
              this.panelPatient = 0;
            }
          }
        } else if (res.data.code === 401) {
          this.unauthorized();
        } else {
        }
      });
    },
    createAppointment(patient) {
      this.sending = true;
      this.loader = true;
      this.$router.push({
        name: "CrearCita",
        params: { idPatient: patient.idPatient },
      });
    },
    checkFirstDirection() {
      if (this.first == 0) {
        if (this.colonies.find((colony) => colony === this.otherColony)) {
        } else {
          this.noColony = true;
          this.formEditar.person.address.colonyKey = "Otro";
        }
        this.first++;
      }
    },
    checkColony() {
      if (this.formEditar.person.address.colonyKey === "Otro") {
        this.noColony = true;
      } else {
        this.noColony = false;
      }
    },
  },
};
</script>
